// STILLINGER — sentralbordmodellen: SKADE snakker med funksjoner, ikke personer.
//
// Eierbeslutninger 2026-08-13:
//   • «alle SKADE-brukere blir til egne matrix-brukere så man kan dele
//     informasjon» — stillingen er kontoen, ikke mennesket.
//   • «hvert område har en TOC (OPS), men det kan være 4-5 operatører i en
//     vanlig ops» — flere bemanner samme stilling samtidig.
//   • «SKADE skal ikke ned på enkeltmann, men ned på lag.»
//   • «Vi tar hele S,G,A,N-rekka 1-10 (10 er innovasjon). Bokstaven avgjøres av
//     ORBAT. På troppsnivå blir det TRSJ, TRSSJT, TRASS, EASS, MEDIC, SAMB.
//     På lagsnivå blir det LF og NLF.»
//
// HVORFOR DETTE ER EN EGEN MODELL: dagens rollekontoer (`@skade_<rolle>`) er
// knyttet til en RBAC-ROLLE, ikke til en plass i organisasjonen. Da finnes det
// ingen måte å adressere «S3 i HV-01-104» — og innkommende svar rutes til ÉN
// SKADE-bruker (den hvis utledede id matcher rommet), så av fire operatører i
// samme TOC ser bare én svaret. En stilling er derimot ORBAT-node × funksjon:
// den kan bemannes av flere samtidig, den overlever vaktskifter, og den er
// adresserbar utenfra uten at noen trenger å vite hvem som sitter der nå.
//
// MODELLEN, kort:
//   • Stillingen EIER rommene. Utgående sendes ALLTID som stillingen, så
//     GNÅ-brukeren ser «HV-01-104 · S3» og aldri fire ulike navn.
//   • Hver operatør på vakt er MEDLEM med sin egen konto, så alle ser hele
//     samtalen i sanntid — ekte Matrix-medlemskap, ikke en kopi.
//   • Hvem av menneskene som faktisk skrev, ligger i hubbens revisjonslogg.
//
// Ren, testbar logikk. Kontoprovisjoneringen skjer i hubben.
import { z } from 'zod';
// ── Stabsrekka (1–10) ──────────────────────────────────────────────────────
//
// Nummeret betyr det samme på tvers av nasjoner og forsvarsgrener — «2» er
// etterretning enten det står S2, G2, A2, N2 eller J2. BOKSTAVEN sier hvilket
// nivå og hvilken gren staben tilhører, og den avgjøres av ORBAT-plasseringen
// (`stabsbokstavFor`). Derfor er de to atskilte felter og ikke én streng:
// «etterretning» skal kunne slås opp uavhengig av hvem som eier staben.
export const Stabsbokstav = z.enum([
    's', // avdelingsstab (bataljon, HV-område)
    'g', // høyere landstab (brigade og HV-distrikt)
    'a', // luftstab
    'n', // sjøstab
    'j', // fellesoperativ stab (FOH)
]);
export const STABSBOKSTAV_NAVN = {
    s: 'Avdelingsstab',
    g: 'Høyere landstab',
    a: 'Luftstab',
    n: 'Sjøstab',
    j: 'Fellesoperativ stab',
};
/** Stabsnummer 1–10. 10 er GNÅ/SKADE-utvidelsen: innovasjon (eiers beslutning). */
export const Stabsnummer = z.union([
    z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5),
    z.literal(6), z.literal(7), z.literal(8), z.literal(9), z.literal(10),
]);
export const STABSNUMMER_NAVN = {
    1: 'Personell',
    2: 'Etterretning og sikkerhet',
    3: 'Operasjoner',
    4: 'Logistikk',
    5: 'Plan',
    6: 'Samband og IKT',
    7: 'Trening og øving',
    8: 'Økonomi og ressurser',
    9: 'Sivilt samvirke',
    10: 'Innovasjon',
};
export const STABSNUMMER_ALLE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
/**
 * Hvilken stabsbokstav hører en ORBAT-node til?
 *
 * Bokstaven avgjøres av ORBAT, som eieren sa — men treet vet ikke selv om en
 * gren er luft eller sjø, så `grenNavn` (nærmeste forsvarsgren-forfader) tas
 * inn av kalleren som HAR treet. Ukjent gren gir 's', som er riktig for hele
 * HV-strukturen og dermed den trygge standarden her.
 */
export function stabsbokstavFor(params) {
    const gren = (params.grenNavn ?? '').toLowerCase();
    if (params.echelon === 'foh')
        return 'j';
    if (/luft/.test(gren))
        return 'a';
    if (/sjø|sjo|marine/.test(gren))
        return 'n';
    // Generalstab-nivå: forsvarsgrenens egen stab er G når den ikke er luft
    // eller sjø (fanget over av `grenNavn`). Eier 2026-08-13: «G på distrikt og
    // for generalstaben».
    if (params.echelon === 'forsvarsgren')
        return 'g';
    // HV-DISTRIKT BRUKER G, IKKE S. Eiers begrunnelse, som er verdt å ha her
    // fordi den forklarer hvorfor og ikke bare hva: «I praksis med 19 områder så
    // er man som en brigader i heimevernet når man er distriktssjef — selv om
    // graden bare er oberstløytnant eller oberst nå i fredstid.» Stabsbokstaven
    // følger altså KOMMANDONIVÅET, ikke sjefens grad i fredstid.
    //
    // Nivåene, som eier bekreftet dem: G på distrikt, brigade og generalstab —
    // S på område og bataljon og nedover — J i fellesoperative hovedkvarter.
    // Jeg hadde satt distrikt til «s»; det var min antakelse, ikke
    // organisasjonens.
    if (params.echelon === 'brigade' || params.echelon === 'distrikt')
        return 'g';
    return 's';
}
// ── Navngitte stillinger (tropp og lag) ────────────────────────────────────
//
// Under avdelingsnivå brukes ikke stabsnummer, men faste funksjonskoder — slik
// avdelingene selv snakker. Kodene OG betegnelsene er eierens (bekreftet
// 2026-08-13: TRASS = troppsassistent, EASS = etterretningsassistent).
export const Stillingskode = z.enum([
    // Kompani/område og oppover
    'SJEF', 'NK',
    // Tropp
    'TRSJ', 'TRSSJT', 'TRASS', 'EASS', 'MEDIC', 'SAMB',
    // Lag
    'LF', 'NLF',
]);
export const STILLINGSKODE_NAVN = {
    SJEF: 'Sjef',
    NK: 'Nestkommanderende',
    TRSJ: 'Troppssjef',
    TRSSJT: 'Troppssersjant',
    TRASS: 'Troppsassistent',
    EASS: 'Etterretningsassistent',
    MEDIC: 'Sanitet',
    SAMB: 'Samband',
    LF: 'Lagfører',
    NLF: 'Nestlagfører',
};
// ── Stillingen ─────────────────────────────────────────────────────────────
/** Stabsstilling: node + bokstav + nummer («HV-01-104 S3»). */
export const StabsstillingSchema = z.object({
    slag: z.literal('stab'),
    nodeId: z.string().min(1).max(64),
    bokstav: Stabsbokstav,
    nummer: Stabsnummer,
});
/** Navngitt stilling: node + funksjonskode («HV-01-104-tropp2 TRSJ»). */
export const KodestillingSchema = z.object({
    slag: z.literal('kode'),
    nodeId: z.string().min(1).max(64),
    kode: Stillingskode,
});
export const StillingSchema = z.discriminatedUnion('slag', [
    StabsstillingSchema,
    KodestillingSchema,
]);
/** Trygt id-ledd: a–z, 0–9 og bindestrek (samme regel som managed-rooms). */
function ledd(s) {
    return s
        .toLowerCase()
        .replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * Matrix-localpart: «HV-01-104» + S3 → `skade_hv-01-104-s3`, og
 * «HV-01-104-tropp2» + TRSJ → `skade_hv-01-104-tropp2-trsj`.
 *
 * Eieren skrev formen som «skade.HV-01-104.TOC-S1». Innholdet er det samme, men
 * punktum og skråstrek er upraktiske i en Matrix-localpart, så skilletegnet er
 * bindestrek — mens VISNINGSNAVNET beholder den lesbare formen.
 */
export function stillingLocalpart(s) {
    const hale = s.slag === 'stab' ? `${s.bokstav}${s.nummer}` : ledd(s.kode);
    return `skade_${ledd(s.nodeId)}-${hale}`;
}
/** Full Matrix-id for stillingen på hjemtjeneren. */
export function stillingMatrixId(s, homeserver = 'matrix.follodefence.com') {
    return `@${stillingLocalpart(s)}:${homeserver}`;
}
/** Lesbart navn — det GNÅ-brukeren ser som avsender, aldri operatørens navn.
 * «HV-01-104 · S3 (Operasjoner)» / «HV-01-104-tropp2 · Troppssjef». */
export function stillingVisningsnavn(s) {
    if (s.slag === 'stab') {
        const merke = `${s.bokstav.toUpperCase()}${s.nummer}`;
        return `${s.nodeId} · ${merke} (${STABSNUMMER_NAVN[s.nummer]})`;
    }
    return `${s.nodeId} · ${STILLINGSKODE_NAVN[s.kode]}`;
}
/** Kort merke til trange flater (lister, kartetiketter): «S3», «TRSJ». */
export function stillingMerke(s) {
    return s.slag === 'stab' ? `${s.bokstav.toUpperCase()}${s.nummer}` : s.kode;
}
/**
 * Hvilke stillinger finnes på et gitt echelon?
 *
 * Eierens regel: **SKADE går ned til LAG, ikke til enkeltmann.** Lag har LF og
 * NLF; tropp har sine seks funksjonskoder; kompani/område og oppover har sjef,
 * NK og hele stabsrekka 1–10. Enkeltmann og makkerpar har INGEN stilling — de
 * finnes bare som *person* i ORBAT-bindingen (`medlemskap.ts`), altså den som
 * vises på kartet og mottar meldinger på telefonen.
 *
 * Skillet er ikke kosmetisk: uten det ville SKADE hatt en adresserbar konto per
 * soldat, og da er det ikke et sentralbord — det er en kontaktliste med tusen
 * oppføringer der ingen vet hvem som svarer.
 */
export function stillingerForNode(params) {
    const { nodeId, echelon } = params;
    const kode = (k) => ({ slag: 'kode', nodeId, kode: k });
    switch (echelon) {
        case 'enkeltmann':
        case 'makkerpar':
            return []; // SKADE går ikke hit
        case 'lag':
            return [kode('LF'), kode('NLF')];
        case 'tropp':
            return [kode('TRSJ'), kode('TRSSJT'), kode('TRASS'), kode('EASS'), kode('MEDIC'), kode('SAMB')];
        default: {
            const bokstav = stabsbokstavFor({ echelon, grenNavn: params.grenNavn });
            return [
                kode('SJEF'),
                kode('NK'),
                ...STABSNUMMER_ALLE.map((n) => ({ slag: 'stab', nodeId, bokstav, nummer: n })),
            ];
        }
    }
}
/** Er dette et echelon SKADE i det hele tatt har stillinger på? */
export function harStillinger(echelon) {
    return stillingerForNode({ nodeId: 'x', echelon }).length > 0;
}
/**
 * Bemanning: hvem sitter på stillingen NÅ.
 *
 * Flere samtidig er det normale — «det kan være 4-5 operatører i en vanlig ops».
 * `paaVakt` er SKADE-bruker-id-er (menneskene); de blir medlemmer av stillingens
 * rom med sin egen konto. Utgående sendes likevel som stillingen.
 */
export const BemanningSchema = z.object({
    stilling: StillingSchema,
    paaVakt: z.array(z.string().max(64)).default([]),
    /** Når bemanningen sist ble endret (ms epoch) — til vaktlogg. */
    endretMs: z.number().int().nonnegative().default(0),
});
/** Er stillingen bemannet? En ubemannet stilling skal VISES som ubemannet, slik
 * at ingen sender en tidskritisk melding til et tomt bord. */
export function erBemannet(b) {
    return !!b && b.paaVakt.length > 0;
}
/** Rekkefølgen en operatør forventer: kommandolinja først, så stabsrekka i
 * nummerorden, så øvrige funksjonskoder. */
export function sorterStillinger(stillinger) {
    const KODERANG = {
        SJEF: 0, NK: 1, TRSJ: 0, TRSSJT: 1, LF: 0, NLF: 1,
        TRASS: 60, EASS: 61, MEDIC: 62, SAMB: 63,
    };
    const rang = (s) => s.slag === 'stab' ? 10 + s.nummer : KODERANG[s.kode];
    return [...stillinger].sort((a, b) => rang(a) - rang(b) || a.nodeId.localeCompare(b.nodeId, 'nb'));
}
//# sourceMappingURL=stilling.js.map